import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRange } from 'react-day-picker';

interface EnhancedDateRangePickerProps {
  value: string | DateRange;
  onValueChange: (value: string | DateRange) => void;
  className?: string;
}

const EnhancedDateRangePicker: React.FC<EnhancedDateRangePickerProps> = ({
  value,
  onValueChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string>(
    typeof value === 'string' ? value : 'custom'
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    typeof value === 'object' ? value : undefined
  );
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(dateRange);
  
  const popoverRef = useRef<HTMLDivElement>(null);

  // Handle preset selection
  const handlePresetSelect = (preset: string) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      onValueChange(preset);
      setIsOpen(false);
    }
  };

  // Handle date range selection
  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setTempDateRange(range);
  };

  // Handle confirm button
  const handleConfirm = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setDateRange(tempDateRange);
      onValueChange(tempDateRange);
      setIsOpen(false);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        // Only close if dates have been selected
        if (tempDateRange?.from && tempDateRange?.to) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen && selectedPreset === 'custom') {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedPreset, tempDateRange]);

  // Format display value
  const getDisplayValue = () => {
    if (selectedPreset === 'custom' && dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, 'dd/MM/yyyy')} - ${format(dateRange.to, 'dd/MM/yyyy')}`;
    }
    
    const presetLabels: Record<string, string> = {
      '7': 'Last 7 days',
      '30': 'Last 30 days',
      '90': 'Last 90 days',
      '365': 'Last 365 days',
      'ytd': 'Year-to-date',
      'all': 'All-time',
      'custom': 'Custom Range',
    };
    
    return presetLabels[selectedPreset] || 'Select period';
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select value={selectedPreset} onValueChange={handlePresetSelect}>
        <SelectTrigger className="w-[200px]">
          <SelectValue>{getDisplayValue()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7">Last 7 days</SelectItem>
          <SelectItem value="30">Last 30 days</SelectItem>
          <SelectItem value="90">Last 90 days</SelectItem>
          <SelectItem value="365">Last 365 days</SelectItem>
          <SelectItem value="ytd">Year-to-date</SelectItem>
          <SelectItem value="all">All-time</SelectItem>
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>

      {selectedPreset === 'custom' && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-[280px] justify-start text-left font-normal',
                !dateRange && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, 'dd/MM/yyyy')} -{' '}
                    {format(dateRange.to, 'dd/MM/yyyy')}
                  </>
                ) : (
                  format(dateRange.from, 'dd/MM/yyyy')
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0" 
            align="start"
            ref={popoverRef}
          >
            <div className="p-4">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={tempDateRange?.from}
                selected={tempDateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={2}
                disabled={(date) => date > new Date()}
              />
              <div className="flex justify-end mt-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={!tempDateRange?.from || !tempDateRange?.to}
                >
                  Select
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default EnhancedDateRangePicker;